import { getAssetPath } from '@/constants/configs';
import {
  defaultConfigs,
  getExtraBasicInfoQuestions as defaultGetExtraBasicInfoQuestions,
  resolveICBWebUrl,
} from '@/constants/tenant-configs/default';

import { QuestionCode } from '@/types/api/pep';
import {
  AppConfigurations,
  ExtraBasicInfoQuestions,
} from '@/types/app-configurations';
import { DocumentType, SigningFormType } from '@/types/document';
import { Language } from '@/types/i18n';
import { User } from '@/types/user';

import { isBusinessPurposeQuestionEnabled, isMinorUser } from '@/utils/user';

export const surinameConfigs: AppConfigurations = {
  ...defaultConfigs,
  links: {
    ...defaultConfigs.links,
    privacyPolicyURL: 'https://www.republicbanksr.com/about/privacy-policy',
    termsAndConditionsURL:
      'https://republiconline.republicbanksr.com/legal.html',
    termsOfUseURL: getAssetPath('/tnc/republiconboard-tnc-sur.pdf'),
    appTermsAndConditionsURL: getAssetPath('/tnc/republiconboard-tnc-sur.pdf'),
    biometricConsentURL: getAssetPath(
      '/bio-consent/republiconboard-biometric-consent-sur.pdf',
    ),
    icbWeb: resolveICBWebUrl(
      'https://republiconlinesr.rfhl.com/ICBankingSR.UI/#/administrationGeneral/login',
    ),
    icbIOS: 'https://apps.apple.com/us/app/republicmobile-sr/id6502415341',
    icbAndroid:
      'https://play.google.com/store/apps/details?id=sric.com.republicprod',
    eFreeURL: 'https://republicbanksr.com/personal/e-free',
    personalChequingURL:
      'https://republicbanksr.com/personal/republic-personal-interest-chequing-account',
    usdSavingsURL:
      'https://republicbanksr.com/personal/republic-foreign-savings-account',
    eurSavingsURL:
      'https://republicbanksr.com/personal/republic-foreign-savings-account',
  },
  defaultCountryCode: '597',
  locale: 'en-SR',
  isoCode: 'SUR',
  dateFormat: 'dd/MM/yyyy',
  defaultCurrency: 'SRD',
  currencyPlacement: {
    [Language.English]: 'after',
    [Language.Dutch]: 'before',
  },
  idCaptureRequirement: 'idCardOrPassport',
  idDocumentTypes: [
    DocumentType.Id,
    DocumentType.Passport,
    DocumentType.IdentityDriverLicense,
  ],
  minorOnlyIdDocumentTypes: [
    DocumentType.BirthCertificate,
    DocumentType.FamilyBook,
  ],
  formsOnEachAccountType: [
    SigningFormType.Authorization,
    SigningFormType.AML4A,
    SigningFormType.SIGNATURE,
  ],
  autoCompleteAdultWithNoDocumentSigning: true,
  availableLanguages: [Language.English, Language.Dutch],
  shouldShowMaritalStatus: true,
  showRiskSectorFlagQuestion: false,
  showSelfEmployedWithRiskSectorFlagQuestion: true,
  showSelfEmployedStartupQuestion: true,
  // TODO: EXC - remove when rolling out to all tenants
  isExcEnabled: false,
  existingCustomer: {
    maxAccountsPerType: 2,
  },
  jointAccountRelationshipOptions: [
    {
      value: 'BIOLOGICAL_MOTHER',
      i18nKey: 'minorOthers.form.relationship.biologicalMother',
    },
    {
      value: 'BIOLOGICAL_FATHER_MARRIED_TO_MOTHER',
      i18nKey: 'minorOthers.form.relationship.biologicalFatherMarriedToMother',
    },
    {
      value: 'BIOLOGICAL_FATHER_NOT_MARRIED_TO_MOTHER',
      i18nKey:
        'minorOthers.form.relationship.biologicalFatherNotMarriedToMother',
    },
    {
      value: 'BIOLOGICAL_FATHER_DIVORCED_FROM_MOTHER',
      i18nKey:
        'minorOthers.form.relationship.biologicalFatherDivorcedFromMother',
    },
    {
      value: 'STEP_PARENT',
      i18nKey: 'minorOthers.form.relationship.stepParent',
    },
    {
      value: 'LEGAL_GUARDIAN',
      i18nKey: 'minorOthers.form.relationship.legalGuardian',
    },
    {
      value: 'GRANDPARENT',
      i18nKey: 'minorOthers.form.relationship.grandparent',
    },
    {
      value: 'AUNT_UNCLE',
      i18nKey: 'minorOthers.form.relationship.auntUncle',
    },
    {
      value: 'OLDER_SIBLING',
      i18nKey: 'minorOthers.form.relationship.olderSibling',
    },
  ],
};

export const getExtraBasicInfoQuestions = (
  user: User,
): ExtraBasicInfoQuestions => {
  if (isMinorUser(user)) {
    return defaultGetExtraBasicInfoQuestions(user);
  }
  return {
    ...defaultGetExtraBasicInfoQuestions(user),
    [QuestionCode.AccountPurpose]: {
      enabled: isBusinessPurposeQuestionEnabled(user),
      question: 'questionDrawer.accountPurpose.question',
      priority: 0,
    },
    [QuestionCode.GrossMonthlyIncome]: {
      enabled: user.monthlyIncome === undefined,
      question: 'questionDrawer.expectedGrossMonthlyIncome',
      priority: 4,
    },
    [QuestionCode.FundingSource]: {
      enabled: user.fundingSource === undefined,
      question: 'questionDrawer.sourceOfFunding',
      priority: 5,
    },
  };
};
